import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, RegisterRequest } from '../types/auth'
import { authService } from '../services/authService'

interface AuthContextType {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  isLoading: boolean  // Alias for loading
  setUser: (user: User | null) => void
  updateUser: (userData: Partial<User>) => void
  login: (name: string, last4: string) => Promise<void>
  register: (data: RegisterFormData) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
}

interface RegisterFormData {
  name: string
  phone: string
  agreeToMarketing: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing authentication on app load
    const initAuth = async () => {
      try {
        const userData = await authService.getCurrentUser()
        setUser(userData)
      } catch (error) {
        // Not authenticated (expected on first load for many users)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [])

  const login = async (name: string, last4: string) => {
    setLoading(true)
    try {
      const { user: userData } = await authService.login(name, last4)
      setUser(userData)
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData: RegisterFormData) => {
    setLoading(true)
    try {
      const registerRequest: RegisterRequest = {
        name: userData.name,
        mobile_number: userData.phone,
        provider: 'email',
      }
      
      const { user: newUser } = await authService.register(registerRequest)
      setUser(newUser)
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      // Redirect to home page
      window.location.href = '/'
    }
  }

  const updateUser = (userData: Partial<User>) => {
    setUser((prev) => prev ? { ...prev, ...userData } : null)
  }

  const refreshToken = async () => {
    try {
      await authService.refreshAccessToken()
      const userData = await authService.getCurrentUser()
      setUser(userData)
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    isLoading: loading,  // Alias for loading
    setUser,
    updateUser,
    login,
    register,
    logout,
    refreshToken,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}